import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { couponSchema } from '../utils/validation';
import { applyCoupon } from '../services/paymentApi';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// COUPON CODES
// To add a new coupon in the future, add an entry here:
//
//   'COUPON_CODE': { discountPercent: 20 }          ← percentage off
//   'COUPON_CODE': { discountAmount: 10 }            ← fixed dollar/unit off
//
// Example:
//   'SAVE20': { discountPercent: 20 },
//   'FLAT10': { discountAmount: 10 },
// ─────────────────────────────────────────────────────────────────────────────
const COUPON_CODES = {
    // Add valid coupon codes here
};

/**
 * Manages coupon code input, validation, and API call.
 *
 * @param {string} token - Auth token from useAuth()
 * @param {number} subtotal - Current order subtotal for server-side validation
 * @param {(amount: number, code: string, label?: string) => void} onApply
 * @param {() => void} onRemove
 * @returns {{
 *   form: import('react-hook-form').UseFormReturn,
 *   appliedCode: string|null,
 *   isLoading: boolean,
 *   handleApply: (data: {couponCode: string}) => Promise<void>,
 *   handleRemove: () => void,
 * }}
 */
export function useCoupon(token, subtotal, onApply, onRemove) {
    const [appliedCode, setAppliedCode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm({
        resolver: zodResolver(couponSchema),
        defaultValues: { couponCode: '' },
        mode: 'onChange',
    });

    const handleApply = useCallback(
        async (data) => {
            setIsLoading(true);
            try {
                const result = await applyCoupon(data.couponCode, subtotal, token);
                if (result.valid) {
                    const discountAmt = result.discountAmount ?? (subtotal * (result.discountPercent ?? 0)) / 100;
                    onApply(discountAmt, data.couponCode);
                    setAppliedCode(data.couponCode);
                    toast.success(`Coupon applied! You saved ${result.discountPercent ? `${result.discountPercent}%` : `$${discountAmt.toFixed(2)}`}`);
                } else {
                    // Check client-side COUPON_CODES as fallback
                    const coupon = COUPON_CODES[data.couponCode.toUpperCase()];
                    if (coupon) {
                        const discountAmt = coupon.discountAmount ?? (subtotal * (coupon.discountPercent ?? 0)) / 100;
                        onApply(discountAmt, data.couponCode);
                        setAppliedCode(data.couponCode);
                        toast.success(`Coupon applied! You saved ${coupon.discountPercent ? `${coupon.discountPercent}%` : `$${discountAmt.toFixed(2)}`}`);
                    } else {
                        toast.error(result.error || 'Invalid coupon code');
                    }
                }
            } catch (err) {
                // API unreachable — check client-side codes only, no automatic discount
                const coupon = COUPON_CODES[data.couponCode.toUpperCase()];
                if (coupon) {
                    const discountAmt = coupon.discountAmount ?? (subtotal * (coupon.discountPercent ?? 0)) / 100;
                    onApply(discountAmt, data.couponCode);
                    setAppliedCode(data.couponCode);
                    toast.success(`Coupon applied! You saved ${coupon.discountPercent ? `${coupon.discountPercent}%` : `$${discountAmt.toFixed(2)}`}`);
                } else {
                    toast.error('Invalid coupon code');
                }
            } finally {
                setIsLoading(false);
            }
        },
        [token, subtotal, onApply]
    );

    const handleRemove = useCallback(() => {
        onRemove();
        setAppliedCode(null);
        form.reset();
        toast.info('Coupon removed');
    }, [onRemove, form]);

    return {
        form,
        appliedCode,
        isLoading,
        handleApply,
        handleRemove,
    };
}
